// TASKS: work given to an agent, a subagent, the team or a saved workflow,
// by the operator or by another agent, and what came back
// (docs/tasks/build-spec.md §7.2, routes/tasks.py). A task is a workflow
// run underneath, so its "How it ran" reads the run's own routes
// (api/workflowRuns.ts). Every key sits under ["tasks", c]; every action
// refreshes the task caches and the subagent runs ("Working now" lists a
// subagent's tasks too, §7.5).
//
// Polling (spec §10.1, as api/jobs.ts): the list every 10 s while a listed
// task is live, else every 30 s; one task every 3 s while it is live. A
// failed read (a 503 included) is "did not load", never an empty list.
// Every write here shows its own error where it was made (the task page,
// the give dialog, Settings → Tasks), so none of them raises the app's toast.
// Types: generated from schemas/tasks.py (src/types/api.gen.ts).

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

type Schemas = components["schemas"];
export type TaskWorkerOut = Schemas["TaskWorkerOut"];
export type TaskAskerOut = Schemas["TaskAskerOut"];
export type TaskDeliveryOut = Schemas["TaskDeliveryOut"];
export type TaskFollowUpOut = Schemas["TaskFollowUpOut"];
export type TaskQuestionOut = Schemas["TaskQuestionOut"];
export type TaskRoundOut = Schemas["TaskRoundOut"];
export type TaskFileOut = Schemas["TaskFileOut"];
export type TaskEventOut = Schemas["TaskEventOut"];
export type TaskActionsOut = Schemas["TaskActionsOut"];
export type TaskSummary = Schemas["TaskSummary"];
export type TaskDetail = Schemas["TaskDetail"];
export type TaskSwitchOut = Schemas["TaskSwitchOut"];
export type TaskListResponse = Schemas["TaskListResponse"];
export type GiveTaskBody = Schemas["GiveTaskBody"];
export type FollowUpBody = Schemas["FollowUpBody"];
export type StopBody = Schemas["StopBody"];
export type TaskActionResponse = Schemas["TaskActionResponse"];
export type TaskPolicyBody = Schemas["TaskPolicyBody"];
export type TaskPolicyOut = Schemas["TaskPolicyOut"];
export type TaskPolicyEntryOut = Schemas["TaskPolicyEntryOut"];
export type TaskRouteOut = Schemas["TaskRouteOut"];
export type TaskRoutingOut = Schemas["TaskRoutingOut"];

/** services/tasks/schema.TaskKind. */
export type TaskKind = Schemas["TaskState"]["kind"];
/** services/tasks/schema.WorkerKind. */
export type TaskWorkerKind = Schemas["TaskWorker"]["kind"];
/** services/tasks/schema.DeliveryState. */
export type TaskDeliveryState = Schemas["OutboxNote"]["state"];
/**
 * services/tasks/schema.TaskStatus (build-spec §1.4). The Out models carry
 * it as a plain `str`, so codegen has no union for it: keep in step with
 * the backend's Literal.
 */
export type TaskStatus = "awaiting_approval" | "queued" | "working" | "waiting" | "paused" | "finished" | "partial" | "failed" | "timed_out" | "stopped";

/** What the list asks the server for (the URL filters it can apply itself). */
export interface TaskQuery {
  worker?: string;
  asker?: string;
  /** TaskStatus values; empty = any. */
  status?: readonly string[];
  kind?: string;
  live?: boolean;
  limit?: number;
  /** Only the tasks this agent gave or is doing (the counts are its own too). */
  agent?: string;
  /** `false` leaves out a schedule's runs that ended with nothing waiting. */
  includeEndedScheduled?: boolean;
}

const LIVE: ReadonlySet<string> = new Set(["awaiting_approval", "queued", "working", "waiting", "paused"]);

/** A task that has not ended (waiting for an OK, queued, working, waiting for an answer, paused). */
export function isLiveTaskStatus(status: string | null | undefined): boolean {
  return !!status && LIVE.has(status);
}

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/tasks`;
const one = (c: string, id: string) => `${base(c)}/${encodeURIComponent(id)}`;

export const taskKeys = {
  all: (c: string | null) => ["tasks", c] as const,
  list: (c: string | null, filters: TaskQuery) => ["tasks", c, "list", filters] as const,
  detail: (c: string | null, id: string | null) => ["tasks", c, "detail", id ?? ""] as const,
  switch: (c: string | null) => ["tasks", c, "switch"] as const,
  policy: (c: string | null) => ["tasks", c, "policy"] as const,
};

/** The query string for a list read ("" = no parameters). */
export function taskQueryString(q: TaskQuery): string {
  const p = new URLSearchParams();
  if (q.worker) p.set("worker", q.worker);
  if (q.asker) p.set("asker", q.asker);
  if (q.status?.length) p.set("status", q.status.join(","));
  if (q.kind) p.set("kind", q.kind);
  if (q.live) p.set("live", "true");
  if (q.limit) p.set("limit", String(q.limit));
  if (q.agent) p.set("agent", q.agent);
  if (q.includeEndedScheduled === false) p.set("include_ended_scheduled", "false");
  const s = p.toString();
  return s ? `?${s}` : "";
}

const notFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * The tasks (newest first), whether giving one is offered here, and the
 * counts. `poll` overrides the page's cadence (10 s while a listed task is
 * live, else 30 s): a badge reads the same list more slowly.
 */
export function useTasks(customerId: string | null, filters: TaskQuery, { poll }: { poll?: number } = {}) {
  return useQuery({
    queryKey: taskKeys.list(customerId, filters),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<TaskListResponse>(`${base(customerId!)}${taskQueryString(filters)}`, { signal }),
    staleTime: 5_000,
    refetchInterval: (query) => poll ?? ((query.state.data?.tasks ?? []).some((t) => isLiveTaskStatus(t.status)) ? 10_000 : 30_000),
    refetchOnWindowFocus: true,
  });
}

/**
 * The live tasks (waiting for an OK, queued, working, waiting for an
 * answer, paused) with the workspace's counts: what Home's and Today's
 * "Working now" and every "needs you" count read, under ONE key so they
 * share one request (features/tasks/useTasksNow).
 */
export const LIVE_TASKS_QUERY: TaskQuery = { live: true, limit: 50 };

/** One task, whole. Polled every 3 s while it is live. */
export function useTask(customerId: string | null, taskId: string | null) {
  return useQuery({
    queryKey: taskKeys.detail(customerId, taskId),
    enabled: !!customerId && !!taskId,
    queryFn: ({ signal }) => apiFetch<TaskDetail>(one(customerId!, taskId!), { signal }),
    refetchInterval: (query) => (isLiveTaskStatus(query.state.data?.status) ? 3_000 : false),
    refetchOnWindowFocus: true,
    staleTime: 2_000,
    // A missing task is an answer, not a blip to retry.
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

/** The task caches, and the subagent runs that list a subagent's tasks. */
function invalidateTasks(qc: QueryClient, customerId: string) {
  void qc.invalidateQueries({ queryKey: taskKeys.all(customerId) });
  void qc.invalidateQueries({ queryKey: ["subagent-runs", customerId] });
}

/**
 * POST …/tasks as the operator: 403 tasks_not_enabled, 409 runtime_unsupported, 404 unknown_worker, 422 bad_minutes …
 * `idempotencyKey` (one per dialog open) makes a repeated send answer with the task the first one made.
 */
export function useGiveTask() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, body, idempotencyKey }: { customerId: string; body: GiveTaskBody; idempotencyKey?: string }) =>
      apiFetch<TaskDetail>(base(customerId), { method: "POST", body, headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined }),
    onSuccess: (task, vars) => qc.setQueryData(taskKeys.detail(vars.customerId, task.task_id), task),
    onSettled: (_d, _e, vars) => invalidateTasks(qc, vars.customerId),
  });
}

interface TaskArgs {
  customerId: string;
  taskId: string;
}

function useTaskAction<V extends TaskArgs>(action: string, body?: (vars: V) => unknown) {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (vars: V) => apiFetch<TaskActionResponse>(`${one(vars.customerId, vars.taskId)}/${action}`, { method: "POST", body: body?.(vars) }),
    onSuccess: (res, vars) => qc.setQueryData(taskKeys.detail(vars.customerId, vars.taskId), res.task),
    onSettled: (_d, _e, vars) => invalidateTasks(qc, vars.customerId),
  });
}

/** A follow-up: queued while a turn runs, or reopens an ended task with its own minutes (409 session_gone, too_many_follow_ups …). */
export function useFollowUpTask() {
  return useTaskAction<TaskArgs & { text: string; minutes?: number | null }>("follow-up", ({ text, minutes }): FollowUpBody => (minutes ? { text, minutes } : { text }));
}

/** Stop the task (its work stops, its asker is told). Confirm first. */
export function useStopTask() {
  return useTaskAction<TaskArgs & { reason?: string }>("stop", ({ reason }): StopBody => ({ reason: reason ?? "" }));
}

/** The operator's OK for a task over the hour an agent may give alone (409 not_awaiting_approval). */
export function useApproveTask() {
  return useTaskAction<TaskArgs>("approve");
}

/** Send the answer again (409 nothing_to_resend once it is delivered or on its way). */
export function useResendTask() {
  return useTaskAction<TaskArgs>("resend");
}

const SWITCH_BUSY: ReadonlySet<string> = new Set(["pending", "running"]);

/**
 * Where the "Agents use tasks only" switch stands (re-rendered instructions,
 * the gateway's reload). Polled every 2 s while it applies, and while
 * `poll` is set (a save that has just started an apply the read may not
 * show yet).
 */
export function useTaskSwitch(customerId: string | null, { poll = false }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.switch(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<TaskSwitchOut>(`${base(customerId!)}/switch`, { signal }),
    refetchInterval: (query) => (poll || SWITCH_BUSY.has(query.state.data?.state ?? "") ? 2_000 : false),
    staleTime: 1_000,
  });
}

/** Who may give work to whom (policy.json; absent agent = everyone). */
export function useTaskPolicy(customerId: string | null) {
  return useQuery({
    queryKey: taskKeys.policy(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<TaskPolicyOut>(`${base(customerId!)}/policy`, { signal }),
    staleTime: 10_000,
  });
}

export function useSaveTaskPolicy() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, body }: { customerId: string; body: TaskPolicyBody }) => apiFetch<TaskPolicyOut>(`${base(customerId)}/policy`, { method: "PUT", body }),
    onSuccess: (policy, vars) => qc.setQueryData(taskKeys.policy(vars.customerId), policy),
  });
}

/** Forget one learned route (who and `give --to auto` stop following it). */
export function useForgetLearnedRoute() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, routeId }: { customerId: string; routeId: string }) =>
      apiFetch<TaskPolicyOut>(`${base(customerId)}/policy/routes/${encodeURIComponent(routeId)}`, { method: "DELETE" }),
    onSuccess: (policy, vars) => qc.setQueryData(taskKeys.policy(vars.customerId), policy),
  });
}

/**
 * Lattice's task routing on or off (PUT …/tasks/routing; state.json
 * tasks_routing): `give --to auto`, learning from a stop and re-give, the
 * repeat offer, and on a tasks-only workspace the TASKS rule that names them
 * (re-rendered in the background: the switch status says where that stands).
 */
export function useSetTaskRouting() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, on }: { customerId: string; on: boolean }) =>
      apiFetch<TaskPolicyOut>(`${base(customerId)}/routing`, { method: "PUT", body: { on } }),
    onSuccess: (policy, vars) => {
      qc.setQueryData(taskKeys.policy(vars.customerId), policy);
      void qc.invalidateQueries({ queryKey: taskKeys.switch(vars.customerId) });
    },
  });
}
